import type { AutomatedResponseState } from '../automated-response/useAutomatedResponseData';
import {
  ACTIVE_RESPONSE_ACTION_TYPE_FILTER_OPTIONS,
  buildActiveResponseSummaryFromItems,
  mapActionTypeToMeta,
  type AutomatedActionKey,
} from '../automated-response/automatedResponseDetailModel';
import type {
  WazuhActiveResponseActionLabelOption,
  WazuhActiveResponseAgentSummaryData,
  WazuhActiveResponseDetail,
  WazuhActiveResponseEndpointItem,
} from 'src/types/mindtime/wazuhActiveResponse';
import { previewPaged } from './previewUtils';

export type { AutomatedActionKey };

export type AutomatedResponseStatus = 'Success' | 'Failed';

/** @deprecated Use WazuhActiveResponseItem in preview fixtures. */
export type AutomatedResponsePreviewRow = {
  id: string;
  timestamp: string;
  trigger: string;
  action: AutomatedActionKey;
  endpoint: string;
  organization: string;
  status: AutomatedResponseStatus;
  durationSec: number;
};

const PREVIEW_TRIGGER_SSHD =
  'sshd: brute force trying to get access to the system. Non existent user.';

const PREVIEW_ITEMS: WazuhActiveResponseDetail[] = [
  {
    id: 'preview-response-1',
    timestamp: '2026-08-31T12:45:00.000Z',
    trigger: 'Ransomware Detected',
    actionType: 'isolate-endpoint',
    endpoint: 'FIN-SRV-02',
    organization: 'Acme Financial',
    status: 'success',
    durationSec: 4,
    systemWazuhAgentId: 'preview-endpoint-1',
    wazuhAgentId: '001',
    hostName: 'fin-srv-02.acme.local',
  },
  {
    id: 'preview-response-2',
    timestamp: '2026-08-31T11:22:00.000Z',
    trigger: 'Malware Detected',
    actionType: 'kill-process',
    endpoint: 'HR-DEV-01',
    organization: 'TechCorp Ltd',
    status: 'success',
    durationSec: 2,
    systemWazuhAgentId: 'preview-endpoint-2',
    wazuhAgentId: '002',
    hostName: 'hr-dev-01.techcorp.local',
  },
  {
    id: 'preview-response-3',
    timestamp: '2026-09-01T10:25:25.000Z',
    executionTime: '2026-09-01T10:25:25.000Z',
    trigger: PREVIEW_TRIGGER_SSHD,
    triggerEvent: PREVIEW_TRIGGER_SSHD,
    actionType: 'block-source-ip',
    actionLabel: 'Block Source IP',
    endpoint: 'mindtime-mindtime-backupxx',
    hostname: 'backupxx.mindtime.nl',
    hostName: 'backupxx.mindtime.nl',
    agentIp: '10.1.16.51',
    organization: 'Mindtime Sub Org',
    organizationName: 'Mindtime Sub Org',
    resellerName: 'Mindtime Reseller Company',
    status: 'success',
    severity: 'high',
    durationSec: 1,
    program: 'active-response/bin/firewall-drop',
    sourceIp: '10.1.253.12',
    sourceUser: 'definitelynotarealuser',
    sourcePort: '39518',
    previousAttempts: [
      'Sep 01 07:22:01 backupxx.mindtime.nl sshd-session[1538901]: Invalid user admin from 10.1.253.12 port 39502',
      'Sep 01 07:23:15 backupxx.mindtime.nl sshd-session[1538922]: Invalid user root from 10.1.253.12 port 39510',
      'Sep 01 07:24:44 backupxx.mindtime.nl sshd-session[1538948]: Invalid user test from 10.1.253.12 port 39514',
    ],
    eventLog:
      'Sep 01 07:25:23 backupxx.mindtime.nl sshd-session[1538964]: Invalid user definitelynotarealuser from 10.1.253.12 port 39518',
    rule: {
      description: PREVIEW_TRIGGER_SSHD,
      id: '5712',
      level: 10,
      firedTimes: 2,
      frequency: 8,
      groups: [
        'nix2',
        'nix2_overwrites',
        'authentication_failures',
        'nix2_21_2_b',
        'nix2_21_2_i',
        'nix2_21_2_j',
        'nix2_23_early_warning',
        'nix2_23_notification',
      ],
    },
    mitreId: 'T1110',
    mitreIds: ['T1110'],
    techniqueNames: ['Brute Force'],
    tactics: ['Credential Access'],
    result: 'Host Blocked by firewall-drop Active Response',
    decoder: 'ar_log_json',
    logLocation: '/var/ossec/logs/active-responses.log',
    systemWazuhAgentId: 'preview-endpoint-2',
    wazuhAgentId: '002',
  },
  {
    id: 'preview-response-4',
    timestamp: '2026-08-30T16:30:00.000Z',
    trigger: 'Suspicious Login',
    actionType: 'disable-user',
    endpoint: 'AUTH-SRV-03',
    organization: 'Nexus Dynamics',
    status: 'failed',
    durationSec: 6,
    systemWazuhAgentId: 'preview-endpoint-3',
    wazuhAgentId: '003',
    hostName: 'auth-srv-03.nexus.local',
  },
  {
    id: 'preview-response-5',
    timestamp: '2026-08-30T14:12:00.000Z',
    trigger: 'Malware Quarantine',
    actionType: 'quarantine-file',
    endpoint: 'LAPTOP-443',
    organization: 'Pinnacle Group',
    status: 'success',
    durationSec: 3,
    systemWazuhAgentId: 'preview-endpoint-1',
    wazuhAgentId: '001',
    hostName: 'laptop-443.pinnacle.local',
  },
];

const PREVIEW_ENDPOINTS: WazuhActiveResponseEndpointItem[] = [
  {
    systemWazuhAgentId: 'preview-endpoint-1',
    wazuhAgentId: '001',
    endpoint: 'FIN-SRV-02',
    hostName: 'fin-srv-02.acme.local',
    organization: 'Acme Financial',
    responses: 4,
    successful: 3,
    failed: 1,
    successRate: 75,
  },
  {
    systemWazuhAgentId: 'preview-endpoint-3',
    wazuhAgentId: '003',
    endpoint: 'APP-SERVER-01',
    hostName: 'app-server-01.nexus.local',
    organization: 'BlueWave Logistics',
    responses: 4,
    successful: 3,
    failed: 1,
    successRate: 75,
  },
  {
    systemWazuhAgentId: 'preview-endpoint-4',
    wazuhAgentId: '004',
    endpoint: 'FIN-PC-17',
    hostName: 'fin-pc-17.acme.local',
    organization: 'Acme Financial',
    responses: 1,
    successful: 0,
    failed: 1,
    successRate: 0,
  },
  {
    systemWazuhAgentId: 'preview-endpoint-5',
    wazuhAgentId: '005',
    endpoint: 'HR-PC-12',
    hostName: 'hr-pc-12.vertex.local',
    organization: 'Vertex Solutions',
    responses: 2,
    successful: 2,
    failed: 0,
    successRate: 100,
  },
];

export const AUTOMATED_RESPONSE_PREVIEW_ROWS: AutomatedResponsePreviewRow[] = PREVIEW_ITEMS.map(
  (item) => ({
    id: item.id ?? '',
    timestamp: item.timestamp ?? '',
    trigger: item.trigger ?? '',
    action: (item.actionType ?? 'isolate-endpoint') as AutomatedActionKey,
    endpoint: item.endpoint ?? '',
    organization: item.organization ?? '',
    status: item.status === 'failed' ? 'Failed' : 'Success',
    durationSec: item.durationSec ?? 0,
  })
);

export const getAutomatedResponsePreviewRows = (): AutomatedResponsePreviewRow[] =>
  AUTOMATED_RESPONSE_PREVIEW_ROWS;

export const getAutomatedResponsePreviewState = (): AutomatedResponseState => ({
  loading: false,
  summary: buildActiveResponseSummaryFromItems(PREVIEW_ITEMS),
  items: previewPaged(PREVIEW_ITEMS, 0, 15),
  endpoints: previewPaged(PREVIEW_ENDPOINTS, 0, 15),
});

export const getAgentActiveResponsePreview = (
  systemWazuhAgentId: string
): { summary: WazuhActiveResponseAgentSummaryData; items: WazuhActiveResponseDetail[] } => {
  const agentId = systemWazuhAgentId.trim();
  const items = PREVIEW_ITEMS.filter(
    (row) => row.systemWazuhAgentId === agentId || row.wazuhAgentId === agentId
  );
  const list = items.length ? items : PREVIEW_ITEMS.slice(0, 1);

  return {
    summary: {
      ...buildActiveResponseSummaryFromItems(list),
      systemWazuhAgentId: agentId,
    },
    items: list,
  };
};

export const getActiveResponsePreviewActionLabels = (
  systemWazuhAgentId?: string
): WazuhActiveResponseActionLabelOption[] => {
  const agentId = systemWazuhAgentId?.trim();
  if (!agentId) {
    return ACTIVE_RESPONSE_ACTION_TYPE_FILTER_OPTIONS;
  }

  const labels = [
    ...new Set(
      getAgentActiveResponsePreview(agentId).items.map(
        (item) => mapActionTypeToMeta(item.actionType).label
      )
    ),
  ]
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));

  return labels.map((label) => ({ label, value: label }));
};

export const getAutomatedResponseDetailPreview = (id: string): WazuhActiveResponseDetail | null => {
  const recordId = id.trim();
  return PREVIEW_ITEMS.find((item) => item.id === recordId) ?? null;
};
